import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import { scale, isAdmin } from '@components/frame';
import { isPhone, isWeb } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import EditableLine from '@components/common/editableLine';

import { inviteUser } from '@reducers/membership';
import { modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const Invite = ({ C, updateData, data = {}, setMode, isSaveActive, inputs, isLoading, submit, hide }) => {
  return (
    <View style={[{ width: '100%', paddingBottom: scale(18) }]}>
      <View
        style={{
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
          zIndex: 1000,
          height: scale(72),
        }}
      >
        <View>
          <TouchableElement onPress={hide}>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { padding: scale(15) }]}>
                <Text style={[{ color: C(pressHoverColor(isPhone ? 'Label/Light/Error' : 'System/Light/Blue')) }, font.Body]}>
                  {i18n.t('Cancel')}
                </Text>
              </View>
            )}
          </TouchableElement>
        </View>
        <View>
          <TouchableElement onPress={submit}>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { padding: scale(15) }]}>
                {isLoading ? (
                  <ActivityIndicator size="small" color="#777777" />
                ) : (
                  <Text style={[{ color: C(pressHoverColor(isSaveActive ? 'Link/Light/Primary' : 'Label/Light/Quinary')) }, font.Body]}>
                    {i18n.t('Send')}
                  </Text>
                )}
              </View>
            )}
          </TouchableElement>
        </View>
      </View>
      <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
        <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t('Invite')}</Text>
      </View>
      <View style={{ width: '100%', paddingLeft: scale(15), paddingRight: scale(15) }}>
        {inputs.map((item, index) => (
          <EditableLine
            key={item.key}
            {...{
              item: {
                value: data[item.key],
                onChange: (v) => updateData(item.key, v),
                ...item,
              },
              bg: modalFieldBg,
              index,
              array: inputs,
              C,
              bottomBorderBox: false,
              topBorder: index !== 0,
              roundTop: index === 0,
              roundBottom: index === inputs.length - 1,
            }}
          />
        ))}
        <View style={{ marginTop: scale(20) }}>
          <TouchableElement onPress={() => (isAdmin ? setMode('role') : () => {})}>
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  {
                    backgroundColor: C(pressHoverColor(modalFieldBg)),
                    height: scale(42),
                    width: '100%',
                    paddingLeft: scale(15),
                    paddingRight: scale(15),
                    borderRadius: scale(SCREEN_BORDER_RADIUS),
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  },
                  boxStyle,
                ]}
              >
                <Text style={[{ color: C('Label/Light/Primary') }, font.Body]}>{i18n.t('Role')}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={[{ color: C('Label/Light/Tertiary') }, font.Body]}>{i18n.t(data.role)}</Text>
                  {isAdmin && (
                    <View style={{ marginLeft: scale(11), marginTop: scale(2) }}>
                      <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
                    </View>
                  )}
                </View>
              </View>
            )}
          </TouchableElement>
        </View>
      </View>
    </View>
  );
};

const Role = ({ C, data = {}, updateData, setMode }) => {
  const { firstName, lastName, role } = data;

  const roles = ['Administrator', 'Manager', 'Operator'];
  return (
    <View
      style={[
        { width: '100%', paddingBottom: scale(18) },
        isPhone && {
          flex: 1,
        },
      ]}
    >
      <View
        style={{
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
          zIndex: 1000,
          height: scale(isPhone ? 72 : 80),
        }}
      >
        <View>
          <TouchableElement onPress={() => setMode('invite')}>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { padding: scale(15), flexDirection: 'row', alignItems: 'center' }]}>
                <Svg icon="chevronLeft" width={scale(isWeb ? 15 : 11, false)} height={scale(isWeb ? 19 : 17, false)} color="Link / Light / Primary" />
                <Text style={[{ color: C(pressHoverColor('System/Light/Blue')), paddingLeft: scale(8) }, font.Body]}>{i18n.t('Back')}</Text>
              </View>
            )}
          </TouchableElement>
        </View>
      </View>
      <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
        <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t('Role')}</Text>
      </View>

      <View style={{ width: '100%', paddingLeft: scale(30) }}>
        <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>
          {i18n.t('Select Role of ')}
          {firstName} {lastName}
        </Text>
      </View>

      <View style={{ paddingLeft: scale(15), paddingRight: scale(15), marginTop: scale(5) }}>
        {roles.map((item, index) => (
          <TouchableElement onPress={() => updateData('role', item)} key={item}>
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  {
                    backgroundColor: C(pressHoverColor(modalFieldBg)),
                    height: scale(42),
                    width: '100%',
                    paddingLeft: scale(15),
                  },
                  index === 0 && {
                    borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
                    borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
                  },
                  index === roles.length - 1 && {
                    borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                    borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
                  },
                  boxStyle,
                ]}
              >
                <View
                  style={[
                    index !== 0 && {
                      borderTopWidth: StyleSheet.hairlineWidth,
                      borderColor: C('Hairline/Light/No trans'),
                    },
                    { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', alignContent: 'center', height: '100%' },
                  ]}
                >
                  <Text style={[{ color: C('Label/Light/Primary') }, font.Body]}>{i18n.t(item)}</Text>
                  <View style={{ paddingRight: scale(15) }}>
                    {item === role && <Svg icon="check" color="Link/Light/Primary" width={scale(19)} height={scale(19)} />}
                  </View>
                </View>
              </View>
            )}
          </TouchableElement>
        ))}
      </View>

      <View style={{ width: '100%', paddingLeft: scale(30), marginTop: scale(15) }}>
        <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('Administrators have all rights')}</Text>
      </View>
      <View style={{ width: '100%', paddingLeft: scale(30), marginTop: scale(15) }}>
        <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('Managers can manage Operators')}</Text>
      </View>
      <View style={{ width: '100%', paddingLeft: scale(30), marginTop: scale(15) }}>
        <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('Operators can only manage themselves')}</Text>
      </View>
    </View>
  );
};

const InviteUser = (props) => {
  const { inviteUser, hide } = props;

  const [isSaveActive, setSaveActive] = useState(false);
  const [mode, setMode] = useState('invite');
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState({
    role: 'Operator',
  });

  const firstField = useRef();

  const submit = async () => {
    if (!isSaveActive || isLoading) {
      return;
    }
    setLoading(true);
    await inviteUser(
      {
        emailAddress: data.email,
        role: data.role,
        userProfile: {
          firstName: data.firstName,
          lastName: data.lastName,
        },
      },
      () => {
        hide();
        setTimeout(() => {
          setData({
            role: 'Operator',
          });
        }, 1000);
      }
    );
    setLoading(false);
  };

  const inputs = [
    {
      key: 'email',
      placeholder: 'Email',
      noArrow: true,
      isLable: false,
      ref: firstField,
    },
    {
      key: 'firstName',
      placeholder: 'First name',
      noArrow: true,
      isLable: false,
      autoCapitalize: 'words',
      autoCorrect: true,
    },
    {
      key: 'lastName',
      placeholder: 'Last name',
      noArrow: true,
      isLable: false,
      autoCapitalize: 'words',
      autoCorrect: true,
    },
  ];

  const updateData = (key, v) => {
    const newData = {
      ...data,
      [key]: v,
    };
    setData(newData);
    const allFiled = inputs.every((i) => newData[i.key]);
    setSaveActive(allFiled);
  };

  const itemProps = {
    ...props,
    setMode,
    updateData,
    data,
    isSaveActive,
    inputs,
    isLoading,
    submit,
    hide,
  };

  const renderContent = () => {
    switch (mode) {
      case 'invite':
        return <Invite {...itemProps} />;
      case 'role':
        return <Role {...itemProps} />;
      default:
        return null;
    }
  };
  return renderContent();
};
export default connect(null, { inviteUser })(ColorWrapper(InviteUser));
